'use client';

import { ToggleGroup as RadixToggleGroup } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn, focusRing } from './cn';

/** The recessed track of a segmented control (and of pill tabs). */
export const segmentTrackClass =
  'inline-flex items-center gap-0.5 rounded-[7px] bg-crow-raised p-0.5';

/**
 * One segment: quiet at rest, lifted out of the track when chosen. `selector` is the attribute
 * that marks the chosen one: Radix's `data-state="on"` (toggle group), `"active"` (tabs) or
 * `"checked"` (a radio group drawn as segments).
 */
export function segmentClass(selector: 'on' | 'active' | 'checked' = 'on') {
  return cn(
    'inline-flex h-[26px] shrink-0 items-center justify-center gap-1.5 rounded-[5px] px-2.5 text-crow-compact font-medium whitespace-nowrap text-crow-text-2 transition-colors duration-[var(--crow-dur-1)] hover:text-crow-text disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
    selector === 'on'
      ? 'data-[state=on]:bg-crow-surface data-[state=on]:text-crow-text data-[state=on]:shadow-crow-1'
      : selector === 'active'
        ? 'data-[state=active]:bg-crow-surface data-[state=active]:text-crow-text data-[state=active]:shadow-crow-1'
        : 'data-[state=checked]:bg-crow-surface data-[state=checked]:text-crow-text data-[state=checked]:shadow-crow-1',
    focusRing,
  );
}

type RootProps = Omit<
  ComponentProps<typeof RadixToggleGroup.Root>,
  'type' | 'value' | 'defaultValue' | 'onValueChange'
>;

/**
 * A row of mutually exclusive choices in one track (System / Light / Dark, Me / My organization):
 * one choice is always on, arrows move between them. For views of the same object with their own
 * panel, use Tabs (`variant="pill"`).
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  className,
  children,
  ...props
}: RootProps & {
  value: T;
  onValueChange: (value: T) => void;
  /** SegmentedControlItem elements. */
  children: ReactNode;
}) {
  return (
    <RadixToggleGroup.Root
      {...props}
      type="single"
      value={value}
      onValueChange={(next) => {
        // Clicking the chosen segment again keeps it chosen.
        if (next) onValueChange(next as T);
      }}
      className={cn(segmentTrackClass, className)}
    >
      {children}
    </RadixToggleGroup.Root>
  );
}

export function SegmentedControlItem({
  className,
  ...props
}: ComponentProps<typeof RadixToggleGroup.Item>) {
  return <RadixToggleGroup.Item className={cn(segmentClass('on'), className)} {...props} />;
}
